/* eslint-disable @typescript-eslint/ban-ts-comment */
import useResizeObserver from "@react-hook/resize-observer";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { AXIS_TYPE } from "constants/chart.constants";
import { ECharts } from "echarts";
import * as echarts from "hd-echarts/echarts";
import { RootState } from "store/rootReducer";
import { useIsDashboardScreenshotMode } from "stores/dashboardScreenshot/useIsDashboardScreenshotMode";
import styled from "styled-components/macro";
import { getProductType } from "utils";

import { useNormalizationUnitCorrection } from "hooks/useNormalizationUnitCorrection";

import { useDashboardDispatch } from "components/dashboard/hooks/useDashboardDispatch";
import useSelectedWellFromStore from "components/data-analyzer/hooks/useSelectedWellFromStore";
import { useChartSettings } from "components/multiphase-chart/context";
import {
  IChartSettingsState,
  initialChartSettingsState
} from "components/multiphase-chart/context/ChartSettingsContext";
import {
  changeChartData,
  changeHiddenSeries,
  changeId,
  toggleIsMouseOver
} from "components/multiphase-chart/context/reducer/chartSettingsReducer";
import { useChartSize } from "components/multiphase-chart/hooks";
import { IMultiPhaseInput } from "components/multiphase-chart/models/shared.models";
import { buildNormalizeByInput } from "components/multiphase-chart/util/buildNormalizeByInput";
import { getDefaultProductStylesMergedWithCustomStyles } from "components/multiphase-chart/util/productStyles";
import { useChartDependencies } from "components/shared/charts/hooks";
import { Indicator } from "components/ui";

import { EntityKind } from "../../../../models/entityKind";
import * as eventHandlers from "../../events";
import * as hooks from "../../hooks";
import ChartAxis from "../axis/ChartAxis";
import ChartLegend from "../legend/ChartLegend";
import ChartToolbar from "../toolbar/Toolbar";
import Screenshot from "./Screenshot";

let arpsWasm: typeof import("wasm/arps") = undefined;
import("wasm/arps.js").then((wasm) => {
  arpsWasm = wasm;
});

const getSessionStorageId = (chartId: string, chartType: string) =>
  `${chartType}::${chartId}`;

// `uwi` pins the chart to one well. Dashboard widgets pass nothing and keep
// following the map selection.
//
// `isDashboardWidget` is what a mount outside the dashboard grid turns off. The
// well card holds the only one: its chart is not part of what the dashboard
// saves, so nothing the user does to it can leave the dashboard modified.
export const MultiPhaseChart = ({
  id,
  onFullscreenToggle,
  type,
  uwi = undefined,
  isDashboardWidget = true
}) => {
  const selectedWell = useSelectedWellFromStore(uwi);
  const {
    globalNormalizeBy,
    filterId,
    userForecastSettings,
    checkedGlobalForecasts,
    syncWells,
    forecastMode,
    useForecastStartDate
  } = useChartDependencies(EntityKind.Well);
  const dashboardDispatch = useDashboardDispatch();

  const chartContainerRef = useRef(null);
  const echartRef = useRef<ECharts>(null);
  const currentlyHighlightedSeries = useRef("");

  const [chartSettings, chartSettingsDispatch] = useChartSettings();

  const correctedLocalNormalizeBy = useNormalizationUnitCorrection(
    chartSettings.settings?.normalizeBy
  );
  const correctedGlobalNormalizeBy = useNormalizationUnitCorrection(globalNormalizeBy);

  const forecastWell = useSelector((state: RootState) => state.app.forecastWell);

  const [numberOfXAxes, setNumberXAxes] = useState(0);
  const [numberOfYAxes, setNumberYAxes] = useState(0);

  /*
   * The echarts instance is created in an effect below this one, and writing a
   * ref re-renders nothing. So the request effect's first pass always sees an
   * empty ref and returns, and it only gets a second pass when one of its own
   * dependencies changes afterwards. A mount where everything is already
   * settled - a well resolved synchronously, a preset that never loads - gets
   * no such change, and the chart stays empty until the user touches
   * something. This flag is the missing re-render.
   */
  const [echartReady, setEchartReady] = useState(false);

  const { hasChartData, setOptionsToChart, createAndSetOptionsToChart, options } =
    hooks.useChartOptionSetter(
      echartRef.current,
      chartSettings.storedPreset,
      chartSettings.settings,
      chartSettings.screenshot,
      chartSettings.legend,
      chartSettings.axisMinMax,
      arpsWasm,
      selectedWell?.Uwi || "",
      chartSettings.hiddenSeries
    );

  /*
   * Build the option, then build it again once the chart has actually drawn.
   *
   * The grid's bottom margin is measured from the RENDERED legend
   * (`getLegendHeight` reads the legend view's bounding box). Before anything
   * has been drawn there is no such box, so it falls back to estimating how
   * many rows the legend will wrap into - and that estimate can reserve
   * several rows more than the legend really takes. The grid then ends that
   * much too high and the plot sits above a band of dead space.
   *
   * `usePostMultiPhase` already builds twice back to back for this reason,
   * which is enough whenever the first build renders synchronously. When it
   * does not, both builds read the same empty view list and the too-tall
   * reservation is what stays on screen until something rebuilds the chart -
   * which is why switching tabs and coming back has been the way to fix it.
   *
   * So the last word goes to a build that cannot run too early: one triggered
   * by the chart's own `finished` event, once per response. Where the earlier
   * builds already had a rendered legend to measure, this one measures the
   * same box and produces the same numbers.
   *
   * Exactly one handler is attached at a time - the previous one is removed
   * before the next is added, so the paired calls above register once between
   * them and nothing accumulates on a chart whose `finished` never arrives
   * (a hidden tab, a zero-size container). It rebuilds from the latest
   * response rather than the one it was created for, so a response that lands
   * while an older handler is still waiting is not redrawn with stale data.
   */
  const latestPostData = useRef(null);
  const rebuildOnFinished = useRef<() => void>(null);

  const applyChartData = useCallback(
    (postData) => {
      latestPostData.current = postData;
      createAndSetOptionsToChart(postData);

      const chart = echartRef.current;
      if (!chart) return;
      if (rebuildOnFinished.current) {
        chart.off("finished", rebuildOnFinished.current);
      }
      const rebuildOnce = () => {
        chart.off("finished", rebuildOnce);
        rebuildOnFinished.current = null;
        createAndSetOptionsToChart(latestPostData.current);
      };
      rebuildOnFinished.current = rebuildOnce;
      chart.on("finished", rebuildOnce);
    },
    [createAndSetOptionsToChart]
  );

  // TODO: isPostLoading seems to have a bug with the library
  // Might need to make a hook to implement custom isLoading if needed
  // Give better name
  const { mutate, data } = hooks.usePostMultiPhase(applyChartData);
  const wrapperRef = useRef(null);

  const screenshotContainerId = `screenshot-container-${id}`;
  useChartSize(wrapperRef);

  const isDashboardMode = useIsDashboardScreenshotMode(id);

  const showLegend =
    chartSettings.legend.visible &&
    (selectedWell || isDashboardMode) &&
    !chartSettings.screenshot.visible &&
    chartSettings.legend.type === "inplot";

  function saveMultiPhaseChartSettingsToSessionStorage(
    chartData: IChartSettingsState
  ): void {
    sessionStorage.setItem(getSessionStorageId(id, type), JSON.stringify(chartData));
  }

  useEffect(() => {
    if (data?.layout?.xAxis2 && data?.layout?.yAxis2) {
      setNumberXAxes(data.layout.xAxis2.length);
      setNumberYAxes(data.layout.yAxis2.length);
    }
  }, [data]);

  useEffect(() => {
    if (!selectedWell) {
      return;
    }

    if (
      !echartRef.current ||
      // TODO: Is this needed?
      !chartSettings.storedPreset.chartTypeId
    ) {
      return;
    }

    saveMultiPhaseChartSettingsToSessionStorage({
      ...chartSettings,
      isSettingsOpen: false,
      // A dashboard save reads this back; hidden products are for the session only.
      hiddenSeries: undefined
    });
    const userForecastData = checkedGlobalForecasts.map((f) => ({
      folderId: f.folderId,
      id: f.id,
      type: f.type
    }));
    const mutateChart: IMultiPhaseInput = {
      filterId: filterId.id,
      products: chartSettings.storedPreset.products.map((product) => ({
        axis: product.axis,
        name: getProductType(product.name).key
      })),
      uwi: selectedWell?.Uwi || "",
      chartStyles: {
        product: getDefaultProductStylesMergedWithCustomStyles(
          chartSettings.storedPreset.style
        )
      },
      userForecastSettings,
      userForecast: userForecastData,
      chartSetting: {
        chartType: chartSettings.storedPreset.chartTypeId,
        rateType: chartSettings.settings.producing ? "Producing" : "Calendar",
        normalizeBy: buildNormalizeByInput(
          chartSettings.settings?.useNormalizeBy,
          correctedLocalNormalizeBy,
          correctedGlobalNormalizeBy
        ),
        forecastMode,
        useForecastStartDate
      },
      useBackfit: chartSettings.settings.backfit,
      combineSameUnitAxis: chartSettings.storedPreset.combineSameUnitAxis
    };

    mutate({ ...mutateChart });
  }, [
    echartReady,
    mutate,
    JSON.stringify(selectedWell),
    userForecastSettings,
    chartSettings.storedPreset,
    chartSettings.axisMinMax,
    chartSettings.settings,
    chartSettings.legend,
    checkedGlobalForecasts,
    chartSettings.screenshot?.visible,
    syncWells,
    useForecastStartDate,
    forecastMode,
    forecastWell
  ]);

  useEffect(() => {
    changeChartData(chartSettingsDispatch, data);
  }, [data, chartSettingsDispatch]);

  useEffect(() => {
    echartRef.current = echarts.init(chartContainerRef.current);
    // The cleanup below runs on unmount only, so there is nothing to set back
    // to false. The `echartRef.current` guard above stays the real check.
    setEchartReady(true);

    return () => {
      if (rebuildOnFinished.current) {
        echartRef.current?.off("finished", rebuildOnFinished.current);
        rebuildOnFinished.current = null;
      }
      latestPostData.current = null;
      echarts.dispose(echartRef.current);
      echartRef.current = null;
    };
  }, [chartContainerRef]);

  useEffect(() => {
    onFullscreenToggle && onFullscreenToggle(chartSettings.isFullscreen ?? false);
  }, [chartSettings.isFullscreen]);

  // sync chart ID with context
  useEffect(() => changeId(chartSettingsDispatch, id), [id, chartSettingsDispatch]);

  // resize chart with viewport
  useResizeObserver(chartContainerRef, () => {
    if (echartRef?.current && options) {
      setOptionsToChart(options);
    }
  });

  const handleMouseEnter = () => {
    toggleIsMouseOver(chartSettingsDispatch, true);
  };

  const handleMouseLeave = () => {
    toggleIsMouseOver(chartSettingsDispatch, false);
  };

  eventHandlers.useHoverOnSeries(
    echartRef.current,
    options,
    currentlyHighlightedSeries,
    setOptionsToChart
  );
  eventHandlers.useHoverOffSeries(
    echartRef.current,
    options,
    currentlyHighlightedSeries,
    setOptionsToChart
  );
  eventHandlers.useLegendHideSeries(
    echartRef.current,
    options,
    chartSettings.hiddenSeries,
    setOptionsToChart,
    (hiddenSeries) => changeHiddenSeries(chartSettingsDispatch, hiddenSeries)
  );

  hooks.useInitialPresetLoader(
    chartSettingsDispatch,
    getSessionStorageId(id, type),
    chartSettings.skipInitialPresetLoad
  );
  hooks.useChartStateForDashboard(dashboardDispatch, isDashboardWidget);

  useEffect(() => {
    setOptionsToChart(options);
  }, [chartSettings.screenshot.preset, chartSettings.screenshot.visible]);

  return (
    <Container
      data-testid="multiphasechart-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={"chart-wrapper"}
      ref={wrapperRef}
      screenshot={chartSettings.screenshot.visible}
      $isDashboardMode={isDashboardMode}>
      <ChartContainer screenshot={chartSettings.screenshot.visible && !isDashboardMode}>
        <EChartWrapper
          className={`ChartPlot ${chartSettings.screenshot.visible ? "screenshot" : ""}`}
          screenshot={
            chartSettings.screenshot.visible && !isDashboardMode
              ? chartSettings.screenshot.preset
              : null
          }>
          <ScreenshotContainer id={screenshotContainerId} />
          <EChart className="echarts-wrapper" ref={chartContainerRef} />
        </EChartWrapper>
      </ChartContainer>

      {!hasChartData && (
        <Indicator
          data-testid={"multiphasechart-no-data-label"}
          data-qa={"multiphasechart-no-data-label"}
          message={"No Data Available"}
        />
      )}
      {!selectedWell && !isDashboardMode && (
        <OverlayContainer
          data-testid="multiphasechart-no-well-selected-label"
          data-qa="multiphasechart-no-well-selected-label">
          <OverlayMessages>
            <NoSelectedWellContainer>
              Please select a well on the map
            </NoSelectedWellContainer>
          </OverlayMessages>
        </OverlayContainer>
      )}
      {showLegend && (
        <ChartLegend
          parentDimensions={{
            width: chartSettings.bounds.width,
            height: chartSettings.bounds.height
          }}
          id="chart-legend"
        />
      )}

      {!chartSettings.screenshot?.visible && <ChartToolbar uwi={uwi} />}
      {(selectedWell || isDashboardMode) && numberOfXAxes > 0 && (
        <>
          {Array.from({ length: numberOfXAxes }, (_, index) => (
            <ChartAxis
              chartData={data}
              key={index}
              axisIndex={index}
              type={AXIS_TYPE.x}
              grid={Array.isArray(options.grid) ? options.grid[0] : options.grid}
              axisOptions={options?.xAxis ? options?.xAxis[index] ?? null : null}
              chartType={chartSettings.storedPreset.chartTypeId}
              isLogScale={chartSettings.settings.logs[index].x}
              axisMinMax={
                chartSettings.axisMinMax[index]
                  ? chartSettings.axisMinMax[index]
                  : initialChartSettingsState.axisMinMax[index]
              }
            />
          ))}
        </>
      )}
      {(selectedWell || isDashboardMode) && numberOfYAxes > 0 && chartSettings && (
        <>
          {Array.from({ length: numberOfYAxes }, (_, index) => (
            <ChartAxis
              chartData={data}
              key={index}
              axisIndex={index}
              type={AXIS_TYPE.y}
              grid={Array.isArray(options.grid) ? options.grid[0] : options.grid}
              axisOptions={options?.yAxis ? options?.yAxis[index] ?? null : null}
              chartType={chartSettings.storedPreset.chartTypeId}
              isLogScale={chartSettings.settings.logs[index].y}
              axisMinMax={
                chartSettings.axisMinMax[index]
                  ? chartSettings.axisMinMax[index]
                  : initialChartSettingsState.axisMinMax[index]
              }
            />
          ))}
        </>
      )}

      <Screenshot containerId={screenshotContainerId} chartInstance={echartRef.current} />
    </Container>
  );
};

const EChart = styled.div`
  width: 100%;
  height: 100%;
  z-index: inherit;
`;

const EChartWrapper = styled.div`
  flex-grow: 1;
  background-color: ${(props) => (props.screenshot ? "#fff" : "inherit")};
  width: ${(props) => (props.screenshot ? props.screenshot.width + "px" : "100%")};
  height: ${(props) => (props.screenshot ? props.screenshot.height + "px" : "100%")};
  transition: all var(--duration);

  .local-normalize-container {
    position: absolute;
    top: 10px;
    right: 20px;
    font-weight: var(--fontWeightMedium);
    align-items: center;
    span {
      margin-right: 8px;
    }
  }

  .chart-tooltip-container {
    max-height: 250px;
    overflow-y: auto;
    /* Not redundant — see WellStatusChart for the full note. A shrink-to-fit
       tooltip plus overflow-y:auto (which forces overflow-x to compute to
       "auto") grows a stray horizontal scrollbar as soon as the vertical one
       appears and takes its width out of the content box. */
    overflow-x: hidden;
  }

  /* prevent header overlap with toast when taking screenshot */
  &.screenshot .chart-toast {
    --top-offset: calc(var(--navbar-height) + var(--chart-toolbar-height));
    top: calc(var(--top-offset) + 20px);
  }

  z-index: 3;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  border-radius: var(--border-radius);
  background-color: ${(props) =>
    props.screenshot && !props.$isDashboardMode ? "rgba(46, 72, 88, 0.24)" : "inherit"};
  box-shadow: ${(props) =>
    props.$isDashboardMode ? "none" : "rgba(var(--color-shadow-rgb), 0.2) 0 0 1px"};
  padding-top: 10px;
  z-index: 0;

  &.hovered {
    box-shadow: rgba(var(--color-shadow-rgb), 0.4) 0 0 1px;
  }

  &.fullscreen {
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: var(--index-fullscreen);
  }
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(204, 204, 204, 0.8);
  z-index: 4;
`;
const OverlayMessages = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  z-index: inherit;
`;

const NoSelectedWellContainer = styled.div`
  color: var(--color-white);
  background-color: var(--color-text);
  border-radius: 5px;
  padding: 2px 20px;
  margin: 0 20px;
`;

const ChartContainer = styled.div`
  justify-self: ${(props) => (props.screenshot ? "center" : "auto")};

  &:hover {
    z-index: 2;
  }
`;

const ScreenshotContainer = styled.div`
  position: absolute;
  display: grid;
  justify-content: center;
  pointer-events: none;
`;
